import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../Shared/Dialog/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../Shared/Select/Select';
import { Spinner } from '../../Shared/Spinner';
import type { InboxConversationBadgeDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { ObservedMetadataKeyDto } from '@/Domain/api/Action/dto/Action.dto';
import { ConversationBadgeKind } from '@/Domain/api/Conversation/dto/ConversationBadge.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useModalStore } from '@/Domain/store/modal.store';

export const CONVERSATION_BADGE_SETTINGS_DIALOG = 'CONVERSATION_BADGE_SETTINGS_DIALOG';

const BADGE_SOURCES: ConversationBadgeKind[] = [
  'topic',
  'custom_topic',
  'sentiment',
  'intent',
  'resolution',
  'language',
  'channel',
  'metadata',
];

// Bounded codes keep the failed-by-reason breakdown usable and raw detail out of analytics.
const saveFailureReason = (error: unknown): string => {
  if (!isAxiosError(error)) return 'unknown';
  if (error.code === 'ECONNABORTED') return 'timeout';
  return error.response ? 'backend_error' : 'network_error';
};

export const ConversationBadgeSettingsDialog = () => {
  const { t } = useTranslation('inbox_v2', { keyPrefix: 'conversation_badge' });
  const { isOpen, closeModal } = useModalStore();
  const open = isOpen(CONVERSATION_BADGE_SETTINGS_DIALOG);
  const { capture } = usePostHogAnalytics();

  const { data, isLoading } = useQuery<InboxConversationBadgeDto>({
    queryKey: [QueryKey.inboxConversationBadge.get],
    enabled: open,
  });

  const mutation = useMutation<
    InboxConversationBadgeDto,
    Error,
    Partial<InboxConversationBadgeDto>
  >({
    mutationKey: [QueryKey.inboxConversationBadge.patch],
  });

  const form = useForm<InboxConversationBadgeDto>({
    defaultValues: { badge_source: null, badge_metadata_key: null },
  });
  const source = form.watch('badge_source');
  const metadataKey = form.watch('badge_metadata_key');
  const isDirty = form.formState.isDirty;

  const { data: observedKeys } = useQuery<ObservedMetadataKeyDto[]>({
    queryKey: [QueryKey.actions.basic.observed_metadata_keys],
    enabled: open && source === 'metadata',
  });

  useEffect(() => {
    if (open && data) {
      form.reset({
        badge_source: data.badge_source,
        badge_metadata_key: data.badge_metadata_key,
      });
    }
  }, [open, data, form]);

  useEffect(() => {
    if (open) {
      capture(PostHogEventEnum.ConversationBadgeSettingsOpened);
    }
  }, [open, capture]);

  // Keep the currently-saved key selectable even if it is not in the recent list.
  const keyOptions: { key: string; example_value: string | null }[] = (() => {
    const fromApi = (observedKeys ?? []).filter(
      (entry) => entry.has_scalar_value ?? entry.example_value !== null,
    );
    if (metadataKey && !fromApi.some((entry) => entry.key === metadataKey)) {
      return [{ key: metadataKey, example_value: null }, ...fromApi];
    }
    return fromApi;
  })();

  const metadataKeyMissing = source === 'metadata' && !metadataKey;

  const handleSubmit = async (values: InboxConversationBadgeDto) => {
    try {
      await mutation.mutateAsync({
        badge_source: values.badge_source,
        badge_metadata_key: values.badge_source === 'metadata' ? values.badge_metadata_key : null,
      });
      capture(PostHogEventEnum.ConversationBadgeSettingsSaved, {
        badge_source: values.badge_source,
        previous_source: data?.badge_source ?? null,
        has_metadata_key: values.badge_source === 'metadata' && Boolean(values.badge_metadata_key),
      });
      form.reset(values);
      toast.success(t('toast.save_success'));
      closeModal();
    } catch (error) {
      capture(PostHogEventEnum.ConversationBadgeSettingsSaveFailed, {
        reason: saveFailureReason(error),
      });
      toast.error(t('toast.save_error'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && closeModal()}>
      <DialogContent className="w-lg" data-testid="conversation-badge-settings-dialog-content">
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogDescription>{t('description')}</DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner className="h-5 w-5" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-foreground">{t('source_label')}</p>
                <Controller
                  control={form.control}
                  name="badge_source"
                  render={({ field }) => (
                    <Select value={field.value ?? undefined} onValueChange={field.onChange}>
                      <SelectTrigger
                        className="w-full"
                        data-testid="conversation-badge-source-trigger"
                      >
                        <SelectValue placeholder={t('source_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {BADGE_SOURCES.filter((badgeSource) => badgeSource !== 'metadata').map(
                          (badgeSource) => (
                            <SelectItem
                              key={badgeSource}
                              value={badgeSource}
                              data-testid={`conversation-badge-source-${badgeSource}`}
                            >
                              {t(`sources.${badgeSource}`)}
                            </SelectItem>
                          ),
                        )}
                        {/* "Conversation data" is set apart: unlike the AI-derived sources above, it reads an arbitrary injected metadata key. */}
                        <SelectSeparator />
                        <SelectItem
                          value="metadata"
                          data-testid="conversation-badge-source-metadata"
                        >
                          {t('sources.metadata')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              {source === 'metadata' && (
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-foreground">{t('metadata_key_label')}</p>
                  <Controller
                    control={form.control}
                    name="badge_metadata_key"
                    render={({ field }) => (
                      <Select value={field.value ?? undefined} onValueChange={field.onChange}>
                        <SelectTrigger
                          className="w-full"
                          data-testid="conversation-badge-key-trigger"
                        >
                          <SelectValue placeholder={t('metadata_key_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {keyOptions.map((entry) => (
                            <SelectItem
                              key={entry.key}
                              value={entry.key}
                              data-testid={`conversation-badge-key-${entry.key}`}
                            >
                              {entry.key}
                              {entry.example_value ? ` · ${entry.example_value}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {keyOptions.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t('no_keys')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="secondary" type="button" onClick={closeModal}>
              {t('cancel')}
            </Button>
            <Button
              variant="dark"
              type="submit"
              disabled={isLoading || mutation.isPending || !isDirty || metadataKeyMissing}
              data-testid="conversation-badge-settings-submit-button"
            >
              {t('save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
